import { useEffect, useState } from "react";

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export function paginationDisplay(
  totalItems: number,
  page: number,
  pageSize: number,
) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  return {
    totalPages,
    page: safePage,
    showingFrom: totalItems === 0 ? 0 : offset + 1,
    showingTo: Math.min(offset + pageSize, totalItems),
    offset,
    limit: pageSize,
  };
}

/** Page/size controls for server-paginated API queries. */
export function useListPagination(resetDeps: unknown[] = []) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [pageSize, ...resetDeps]);

  const offset = (page - 1) * pageSize;

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    offset,
    limit: pageSize,
  };
}

/** Page/size state for server-paginated lists (legacy alias). */
export function usePaginationState(
  totalItems: number,
  resetDeps: unknown[] = [],
) {
  const controls = useListPagination(resetDeps);
  const display = paginationDisplay(
    totalItems,
    controls.page,
    controls.pageSize,
  );

  useEffect(() => {
    if (controls.page !== display.page) {
      controls.setPage(display.page);
    }
  }, [controls.page, display.page, controls.setPage]);

  return {
    ...controls,
    ...display,
    totalPages: display.totalPages,
    showingFrom: display.showingFrom,
    showingTo: display.showingTo,
  };
}

/** Slice a loaded array client-side (search/filter tabs, settings catalogs). */
export function useClientPagination<T>(
  items: T[],
  resetDeps: unknown[] = [],
) {
  const pagination = usePaginationState(items.length, resetDeps);
  const pageItems = items.slice(
    pagination.offset,
    pagination.offset + pagination.limit,
  );
  return { ...pagination, pageItems };
}
